import type { Box } from "../camera/framing"
import { DETECTOR, type DetectedBox } from "../camera/detector"
import { buildFeatures, EMBEDDER_URL, HEAD_URL, headProbs, parseHead, type ViewHead, type ViewProbs } from "./head"
import { taillightHint, type EndHint } from "./taillights"

// Everything the camera runs on the phone, in one place that works on the
// page or in a worker (vision-worker.ts, so the camera never stutters):
//
//   detector   the car in the live picture (EfficientDet-Lite0, VIDEO mode)
//   segmenter  their car's exact shape: the one object under a point
//              ("MagicTouch" interactive segmenter), not the cars round it
//   brain      which view of the car a picture shows: its own car finder
//              (IMAGE mode), MobileNet-v3 embeddings seven ways, our head
//
// Each part loads on its own; one that can't (or is too slow on this phone)
// is just missing, and the camera does without it. All Google models are
// Apache-2.0, self-hosted (public/mediapipe/README.md).
//
// CPU only. The GPU path needs a page (MediaPipe looks for `document`), which
// a worker doesn't have, and in testing it found no car on the GPU anyway.
// Worth another look with timings from real phones (scripts/walks).

export interface CarMask {
  // 1 = the car under the point, 0 = anything else; width × height, row by row.
  mask: Uint8Array
  width: number
  height: number
}

export interface ViewReading {
  probs: ViewProbs
  // The car in the picture (0–1), null when none was found.
  box: Box | null
  taillights: EndHint
  ms: number
}

export type VisionImage = ImageBitmap | HTMLCanvasElement | OffscreenCanvas | HTMLVideoElement

// How long each part took lately (the median of its last few runs), null
// before its first run.
export interface VisionTimings {
  detect: number | null
  segment: number | null
  view: number | null
}

export interface VisionEngine {
  caps: { detector: boolean; segmenter: boolean; brain: boolean }
  // The car finder couldn't start (not just too slow): in a worker, worth
  // trying the page instead. (Not for the other parts: the camera works
  // without them, and moving the car finder to the page for them would
  // bring back the stutter the worker is there to prevent.)
  failed: boolean
  // It keeps throwing on every frame (a lost WebGL context, say): as good
  // as gone.
  broken(): boolean
  // How often the live camera may ask the detector (it keeps to a share of
  // the time on a slow phone).
  intervalMs: number
  // Boxes 0–1 of the image.
  detect(image: VisionImage, t: number): DetectedBox[]
  // `point`: where the car is (0–1 of the image).
  segment(image: VisionImage, point: { x: number; y: number }): CarMask | null
  // The live picture (no taillight check: the live corner check doesn't use it).
  readFrame(image: VisionImage): ViewReading | null
  // A photo taken or picked, with the taillight check.
  readPhoto(photo: Blob): Promise<ViewReading | null>
  timings(): VisionTimings
  dispose(): void
}

export const SEGMENTER = {
  MODEL_URL: "/mediapipe/magic_touch.tflite",
  // Slower than this per run and it isn't worth the time.
  GIVE_UP_MS: 450,
} as const

// The car finder throwing this many frames in a row: it's not coming back.
const BROKEN_AFTER = 5

const BRAIN = { SCORE: 0.3, MAX: 5, MIN_AREA: 0.04, PAD: 0.04, WORK_WIDTH: 500 } as const

type Vision = typeof import("@mediapipe/tasks-vision")

interface MpDetection {
  boundingBox?: { originX: number; originY: number; width: number; height: number }
  categories: Array<{ score: number; categoryName: string }>
}
interface MpVideoDetector {
  detectForVideo(image: VisionImage, timestamp: number): { detections: MpDetection[] }
  close(): void
}
interface MpImageDetector {
  detect(image: VisionImage): { detections: MpDetection[] }
  close(): void
}
interface MpSegmenter {
  segment(
    image: VisionImage,
    roi: { keypoint: { x: number; y: number } },
    callback: (result: { categoryMask?: { width: number; height: number; getAsUint8Array(): Uint8Array } }) => void,
  ): void
  close(): void
}
interface MpEmbedder {
  embed(
    image: VisionImage,
    options: { regionOfInterest: { left: number; top: number; right: number; bottom: number } },
  ): { embeddings: Array<{ floatEmbedding?: number[] }> }
  close(): void
}

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas
type AnyContext = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

function makeCanvas(w: number, h: number): AnyCanvas {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h)
  const c = document.createElement("canvas")
  c.width = w
  c.height = h
  return c
}

// A plain grey picture, for timing the models at startup.
function blankCanvas(w: number, h: number): AnyCanvas {
  const c = makeCanvas(w, h)
  const ctx = c.getContext("2d") as AnyContext | null
  if (ctx) {
    ctx.fillStyle = "#777"
    ctx.fillRect(0, 0, w, h)
  }
  return c
}

const sizeOf = (image: VisionImage) =>
  "videoWidth" in image ? { w: image.videoWidth, h: image.videoHeight } : { w: image.width, h: image.height }

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b)
  return s[Math.floor(s.length / 2)] ?? Infinity
}

function boxesOf(detections: MpDetection[], W: number, H: number): DetectedBox[] {
  return detections
    .filter((d) => d.boundingBox && d.categories[0])
    .map((d) => ({
      box: { x: d.boundingBox!.originX / W, y: d.boundingBox!.originY / H, w: d.boundingBox!.width / W, h: d.boundingBox!.height / H },
      score: d.categories[0].score,
      label: d.categories[0].categoryName,
    }))
}

async function loadVision(wasmBase: string) {
  const vision: Vision = await import("@mediapipe/tasks-vision")
  const fileset = await vision.FilesetResolver.forVisionTasks(wasmBase)
  return { vision, fileset }
}

// In a worker, each model gets its own OffscreenCanvas: left to itself,
// MediaPipe only trusts OffscreenCanvas in browsers it knows (not Chrome or
// Firefox on an iPhone) and otherwise asks for a `document`, which a worker
// doesn't have. On the page it picks its own.
const glCanvas = () =>
  typeof document === "undefined" && typeof OffscreenCanvas !== "undefined" ? { canvas: new OffscreenCanvas(1, 1) } : {}

async function videoDetector(v: Vision, fileset: Awaited<ReturnType<typeof loadVision>>["fileset"]) {
  return (await v.ObjectDetector.createFromOptions(fileset, {
    ...glCanvas(),
    baseOptions: { modelAssetPath: DETECTOR.MODEL_URL, delegate: "CPU" },
    runningMode: "VIDEO",
    scoreThreshold: DETECTOR.SCORE_THRESHOLD,
    maxResults: DETECTOR.MAX_RESULTS,
    categoryAllowlist: [...DETECTOR.LABELS],
  })) as unknown as MpVideoDetector
}

// Timed runs on a frame (after one warm-up); the median.
function timeDetector(mp: MpVideoDetector, image: VisionImage, runs = 5): number {
  let ts = Math.round(performance.now())
  mp.detectForVideo(image, ts++)
  const times: number[] = []
  for (let i = 0; i < runs; i++) {
    const start = performance.now()
    mp.detectForVideo(image, ts++)
    times.push(performance.now() - start)
  }
  return median(times)
}

export async function createEngine(opts: { wasmBase: string }): Promise<VisionEngine | null> {
  let rt: Awaited<ReturnType<typeof loadVision>>
  try {
    rt = await loadVision(opts.wasmBase)
  } catch {
    return null
  }
  const { vision, fileset } = rt

  // The detector, timed on a blank frame: too slow and the camera goes guided.
  let detector: MpVideoDetector | null = null
  let detectMs = Infinity
  let failed = false
  try {
    detector = await videoDetector(vision, fileset)
    detectMs = timeDetector(detector, blankCanvas(320, 568))
    if (detectMs > DETECTOR.PERF_GIVE_UP_MS) {
      detector.close()
      detector = null
    }
  } catch {
    detector?.close()
    detector = null
    failed = true
  }
  // The segmenter and the brain, side by side.
  const [segR, brainR] = await Promise.allSettled([
    (async () => {
      const mp = (await vision.InteractiveSegmenter.createFromOptions(fileset, {
        ...glCanvas(),
        baseOptions: { modelAssetPath: SEGMENTER.MODEL_URL, delegate: "CPU" },
        outputCategoryMask: true,
        outputConfidenceMasks: false,
      })) as unknown as MpSegmenter
      // (Drawn on: a canvas never drawn on reads as no picture at all.)
      const probe = blankCanvas(256, 256)
      const runs: number[] = []
      for (let i = 0; i < 4; i++) {
        const start = performance.now()
        mp.segment(probe, { keypoint: { x: 0.5, y: 0.5 } }, () => {})
        if (i > 0) runs.push(performance.now() - start)
      }
      if (median(runs) > SEGMENTER.GIVE_UP_MS) {
        mp.close()
        return null
      }
      return mp
    })(),
    (async () => {
      const [det, emb, head] = await Promise.all([
        vision.ObjectDetector.createFromOptions(fileset, {
          ...glCanvas(),
          baseOptions: { modelAssetPath: DETECTOR.MODEL_URL, delegate: "CPU" },
          runningMode: "IMAGE",
          scoreThreshold: BRAIN.SCORE,
          maxResults: BRAIN.MAX,
          categoryAllowlist: [...DETECTOR.LABELS],
        }) as unknown as Promise<MpImageDetector>,
        vision.ImageEmbedder.createFromOptions(fileset, {
          ...glCanvas(),
          baseOptions: { modelAssetPath: EMBEDDER_URL, delegate: "CPU" },
          runningMode: "IMAGE",
          l2Normalize: true,
          quantize: false,
        }) as unknown as Promise<MpEmbedder>,
        fetch(HEAD_URL).then(async (r) => {
          if (!r.ok) throw new Error(`car-view head: ${r.status}`)
          return parseHead(await r.arrayBuffer())
        }),
      ])
      return { det, emb, head }
    })(),
  ])
  const segmenter = segR.status === "fulfilled" ? segR.value : null
  let brain = brainR.status === "fulfilled" ? brainR.value : null

  const recent: Record<keyof VisionTimings, number[]> = { detect: [], segment: [], view: [] }
  const note = (part: keyof VisionTimings, ms: number) => {
    const xs = recent[part]
    xs.push(ms)
    if (xs.length > 9) xs.shift()
  }
  let lastTs = 0
  // Detector runs that threw, in a row.
  let throwsInRow = 0
  const work = makeCanvas(BRAIN.WORK_WIDTH, BRAIN.WORK_WIDTH)

  // The car-view brain on a picture drawn at the training photos' width.
  const readImage = (image: VisionImage, withTaillights: boolean, b: NonNullable<typeof brain>): ViewReading | null => {
    const start = performance.now()
    const { w, h } = sizeOf(image)
    if (!w || !h) return null
    work.width = BRAIN.WORK_WIDTH
    work.height = Math.max(1, Math.round((h * BRAIN.WORK_WIDTH) / w))
    const ctx = work.getContext("2d", { willReadFrequently: true }) as AnyContext | null
    if (!ctx) return null
    ctx.drawImage(image, 0, 0, work.width, work.height)
    const car = readCar(b.det, work)
    const box = car?.box ?? { x: 0, y: 0, w: 1, h: 1 }
    const roi = (l: number, r: number) => ({
      regionOfInterest: { left: box.x + box.w * l, top: box.y, right: box.x + box.w * r, bottom: box.y + box.h },
    })
    const emb = (l: number, r: number) => b.emb.embed(work, roi(l, r)).embeddings[0]?.floatEmbedding ?? []
    const features = buildFeatures(
      {
        whole: emb(0, 1),
        left: emb(0, 0.5),
        right: emb(0.5, 1),
        quarters: [emb(0, 0.25), emb(0.25, 0.5), emb(0.5, 0.75), emb(0.75, 1)],
      },
      { hasCar: !!car, score: car?.score ?? 0, aspect: (box.w * work.width) / (box.h * work.height), area: box.w * box.h },
    )
    const probs = headProbs(b.head as ViewHead, features)
    const taillights =
      car && withTaillights
        ? taillightHint({ data: ctx.getImageData(0, 0, work.width, work.height).data, width: work.width, height: work.height }, car.box)
        : null
    return { probs, box: car?.box ?? null, taillights, ms: performance.now() - start }
  }

  // The brain, tried once on a plain picture: a browser that loads it but
  // can't run it (no WebGL where it runs) is caught here, not on a photo.
  if (brain) {
    let works = false
    try {
      works = readImage(blankCanvas(320, 240), false, brain) !== null
    } catch {
      works = false
    }
    if (!works) {
      brain.det.close()
      brain.emb.close()
      brain = null
    }
  }

  return {
    caps: { detector: !!detector, segmenter: !!segmenter, brain: !!brain },
    failed,
    broken: () => throwsInRow >= BROKEN_AFTER,
    intervalMs: detectMs > DETECTOR.PERF_SLOW_MS ? DETECTOR.SLOW_INTERVAL_MS : DETECTOR.MIN_INTERVAL_MS,
    detect(image, t) {
      if (!detector) return []
      // MediaPipe needs strictly increasing timestamps.
      const ts = Math.max(lastTs + 1, Math.round(t))
      lastTs = ts
      const start = performance.now()
      let found: MpDetection[] = []
      try {
        found = detector.detectForVideo(image, ts).detections
        throwsInRow = 0
      } catch {
        found = []
        throwsInRow++
      }
      note("detect", performance.now() - start)
      const { w, h } = sizeOf(image)
      return boxesOf(found, w || 1, h || 1)
    },
    segment(image, point) {
      if (!segmenter) return null
      const start = performance.now()
      let out: CarMask | null = null
      try {
        segmenter.segment(image, { keypoint: point }, (result) => {
          const m = result.categoryMask
          if (!m) return
          const labels = m.getAsUint8Array()
          // Whatever value the point itself got is the object's.
          const px = Math.min(m.width - 1, Math.max(0, Math.floor(point.x * m.width)))
          const py = Math.min(m.height - 1, Math.max(0, Math.floor(point.y * m.height)))
          const object = labels[py * m.width + px]
          const mask = new Uint8Array(labels.length)
          for (let i = 0; i < labels.length; i++) mask[i] = labels[i] === object ? 1 : 0
          out = { mask, width: m.width, height: m.height }
        })
      } catch {
        out = null
      }
      note("segment", performance.now() - start)
      return out
    },
    readFrame(image) {
      if (!brain) return null
      try {
        const r = readImage(image, false, brain)
        if (r) note("view", r.ms)
        return r
      } catch {
        return null
      }
    },
    async readPhoto(photo) {
      if (!brain) return null
      try {
        const bitmap = await createImageBitmap(photo)
        const r = readImage(bitmap, true, brain)
        bitmap.close()
        return r
      } catch {
        return null
      }
    },
    timings: () => ({
      detect: recent.detect.length ? median(recent.detect) : null,
      segment: recent.segment.length ? median(recent.segment) : null,
      view: recent.view.length ? median(recent.view) : null,
    }),
    dispose() {
      detector?.close()
      segmenter?.close()
      brain?.det.close()
      brain?.emb.close()
    },
  }
}

// The brain's own car finder: the biggest, surest car, padded a little.
function readCar(detector: MpImageDetector, canvas: AnyCanvas) {
  const W = canvas.width
  const H = canvas.height
  let best: { x: number; y: number; w: number; h: number; s: number } | null = null
  let bestValue = 0
  for (const d of detector.detect(canvas).detections) {
    const b = d.boundingBox
    const s = d.categories[0]?.score ?? 0
    if (!b) continue
    const value = ((b.width * b.height) / (W * H)) * s
    if (value > bestValue) {
      bestValue = value
      best = { x: b.originX / W, y: b.originY / H, w: b.width / W, h: b.height / H, s }
    }
  }
  if (!best || best.w * best.h <= BRAIN.MIN_AREA) return null
  const x0 = Math.max(0, best.x - BRAIN.PAD)
  const y0 = Math.max(0, best.y - BRAIN.PAD)
  const x1 = Math.min(1, best.x + best.w + BRAIN.PAD)
  const y1 = Math.min(1, best.y + best.h + BRAIN.PAD)
  return { box: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 }, score: best.s }
}
